import { useTranslator } from '@bluebeam/react-i18n';
import { BannerContext } from 'components/Routes/BannerContext';
import BreadcrumbsOfAppState from 'components/BreadcrumbsOfAppState';
import LoadingAnimation from 'components/LoadingAnimation';
import Navigation from 'components/Navigation';
import { FeatureFlag, useFeatureFlags } from 'featureFlags';
import React, { useEffect, useState, useContext } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import * as toast from 'utils/toast';
import withAppStore, { WithAppStoreProps } from 'utils/withAppStore';
import StudioProjectsList from './StudioProjectsList';

interface RouteWithPassedProps extends RouteComponentProps {
  location: {
    pathname: string;
    hash: string;
    search: string;
    state: {
      selectedProject: string;
    };
  };
}

interface Props extends WithAppStoreProps, RouteWithPassedProps {}

const StudioTab: React.FC<Props> = (props: Props) => {
  const { height: bannerHeight } = useContext(BannerContext);
  const { [FeatureFlag.ImportFromStudio]: isImportFromStudioEnabled } =
    useFeatureFlags();
  const { appState, history, actions } = props;
  const [loaded, setLoaded] = useState(false);
  const { translate } = useTranslator(['buttons', 'toastText', 'studioTab']);

  const confirmFileSelection = async () => {
    setLoaded(false);
    await actions.importStudioFiles();

    history.push(`/project/${appState.selectedProject}/drawings/uploads`);
    toast.success(
      translate('toastText:filePendingUploadProvider.message'),
      translate('toastText:filePendingUploadProvider.title', {
        providerName: 'STUDIO',
      })
    );
    window.setTimeout(actions.getProcessingStatuses, 1000);
  };

  const handleCancelClick = async (): Promise<void> => {
    await actions.resetBreadcrumbs();
    await actions.resetStudioView();
    await actions.setStudioSelectedFiles([]);
    history.push(`/project/${appState.selectedProject}/drawings/published`);
  };

  useEffect(() => {
    if (isImportFromStudioEnabled && appState.studioProjects.size !== 0) {
      setLoaded(true);
    } else {
      history.push(`/project/${appState.selectedProject}/drawings/published`);
    }
  }, []);

  return (
    <Navigation>
      <div id="integrations-upload" className="oh h100">
        <div className="px4 pt4 h100">
          <div className="row justify-between items-center pb4 bb-3">
            <div className="bold font-size-5">
              {translate('studioTab:itemsSelected', {
                count: appState.studioSelectedFiles.length,
              })}
            </div>
            <div className="row justify-between items-center">
              <button
                id="integrations-modal-sync-btn"
                className="row items-center btn-sm btn-link mr3"
                onClick={handleCancelClick}
              >
                {translate('buttons:cancel')}
              </button>
              <button
                id="integrations-modal-sync-btn"
                className="row items-center btn-sm btn-primary mr3"
                onClick={confirmFileSelection}
                disabled={
                  appState.studioSelectedFiles.length === 0 &&
                  appState.studioSelectedFolders.length === 0
                }
              >
                {translate('buttons:confirmSelection')}
              </button>
            </div>
          </div>
          <div className="my3">
            <BreadcrumbsOfAppState providerType="studio" />
          </div>
          <div
            className="oh"
            style={{ height: `calc(100vh - 226px - ${bannerHeight}px)` }}
          >
            {loaded ? (
              <StudioProjectsList pdfOnly folderSelectable />
            ) : (
              <div className="column h100 w100 b-3 bgd-light-grey-2 justify-center items-center">
                <LoadingAnimation pageName="Loading Studio Projects" />
              </div>
            )}
          </div>
        </div>
      </div>
    </Navigation>
  );
};

export default withAppStore<Props>(StudioTab);
